import { scaleLinear } from 'd3-scale';
import { max, min } from 'd3-array';

import { round } from 'components/data-visualization/LineChart/helpers/math';

const noop = (): void => {}; // eslint-disable-line @typescript-eslint/no-empty-function

interface IYScaleProps {
  data: any;
  svgHeight: any;
  margin: any;
  yKey: string;
  heightBottomLabel: number;
  offset?: number;
  isPosition?: boolean;
}

const getYScale = ({
  data,
  svgHeight,
  margin,
  yKey,
  heightBottomLabel,
  offset = 0,
  isPosition = false,
}: IYScaleProps): any => {
  const height = round(svgHeight - heightBottomLabel - margin.topHorizontalAxe);
  const notNullYValues = data.map((item: any) => item[yKey]);
  const minYValue =
    notNullYValues && notNullYValues.length > 0 ? min(notNullYValues) : 0;
  const maxYValue =
    notNullYValues && notNullYValues.length > 0 ? max(notNullYValues) : 0;
  const range = isPosition ? [offset, height] : [height, offset];

  /** In case of straight horizontal line with the same y value for position chart, we need to calculate domain manually */
  const domainRange =
    minYValue === maxYValue
      ? [0, Number(maxYValue) + Number(maxYValue)]
      : [minYValue, maxYValue];

  /** Get scale to get a step between ticks, recalculate min and max values, generate a scale using updated min and max values */
  const currentScale = scaleLinear()
    .domain(domainRange as any)
    .range(range);

  if (minYValue === maxYValue) return currentScale;

  const initialTicks = currentScale.ticks();
  const ticks = isPosition
    ? [
        ...new Set(
          initialTicks.map((tick: number) => Number(String(tick).split('.')[0]))
        ),
      ]
    : initialTicks;
  /** The number of steps (an interval between ticks) for an indent above and below a chart. */
  const getNumberOfSteps = (isPosition: boolean, isMin = false): number =>
    !isPosition ? 10 : isMin ? 5 : 3;
  const newMin =
    Number(minYValue) -
    getNumberOfSteps(isPosition, true) * Math.abs(ticks[1] - ticks[0]);
  const newMax =
    Number(maxYValue) +
    getNumberOfSteps(isPosition) *
      Math.abs(ticks[ticks.length - 1] - ticks[ticks.length - 2]);
  /** For positions min value can be only 1, not 0 */
  const minVal = isPosition
    ? newMin < 1
      ? 1
      : newMin
    : newMin < 0
    ? 0
    : newMin;
  return scaleLinear()
    .domain([minVal, Math.ceil(newMax) || maxYValue] as any)
    .range(range);
};

const getYScales = ({
  data,
  svgHeight,
  margin,
  heightBottomLabel,
  chartPending,
  isPositionsDisplayed,
  svgHeightPositions,
}: any): any => {
  if (chartPending && !data) {
    return {
      positionYScale: noop,
      streamYScale: noop,
    };
  }

  const positionYScale =
    data.position &&
    data.position.length > 0 &&
    getYScale({
      data: data.position,
      svgHeight: svgHeightPositions,
      margin,
      yKey: 'position',
      heightBottomLabel: heightBottomLabel + margin.topHorizontalAxe,
      isPosition: true,
    });
  const streamYScale =
    data.stream &&
    data.stream.length > 0 &&
    getYScale({
      data: data.stream,
      svgHeight,
      margin,
      yKey: 'streamCount',
      heightBottomLabel: heightBottomLabel + margin.topHorizontalAxe,
      offset: isPositionsDisplayed ? svgHeight / 2 : 0,
    });

  return {
    positionYScale,
    streamYScale,
  };
};

export default getYScales;
